import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Api} from "../_interfaces/api";
import {
  ContactFieldComporatorList,
  PlatformAppConfig,
  PlatformCampaign,
  PlatformContactField, PlatformForm,
  PlatformLink,
  PlatformList,
  PlatformMessage,
  PlatformSegment,
  Sender
} from "../_interfaces/platform";
import {filter, map, Observable, of, shareReplay, Subject, switchMap, takeUntil, timer, toArray} from "rxjs";
import {catchError} from "rxjs/operators";
import {skipModalError} from "@app/shared/_models/auth";
import {API_BASE_URL} from "@app/shared/_factories/apibaseurl.factory";

@Injectable({
  providedIn: 'root'
})
export class EmailchefService {

  private readonly apiUrl = inject(API_BASE_URL);
  private readonly apiPrefix = `${this.apiUrl}/emailchef`;

  private _comparators$: Observable<Api.Response<ContactFieldComporatorList>>;
  private http = inject(HttpClient);

  getAppConfig() {

    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('.emailchef.com')
    ) {
      return of(null);
    }

    return this
      .http
      .get<{
        config: PlatformAppConfig
      }>(
        `${this.apiPrefix}/appconfig`, {
          params: {
            domain_url: window.location.protocol + "//" + window.location.hostname
          }
        }
      ).pipe(
        map(({config}) => config),
        shareReplay()
      );
  }

  getAccountInfo() {
    return this
      .http
      .get<Api.Response<{ account_id: number, lang: string, has_autoresponders: boolean }>>(
        `${this.apiPrefix}/accountinfo`
      ).pipe(
        shareReplay()
      );
  }

  lists() {
    return this
      .http
      .get<Api.Response<any>>(
        `${this.apiPrefix}/list`
      );
  }

  campaigns$(
    flows: boolean = false
  ) {
    return this
      .http
      .get<Api.Response<PlatformMessage[]>>(
        `${this.apiPrefix}/campaign`, {
          params: {
            campaign_type: flows ? 'flow' : 'normal'
          }
        }
      );
  }

  flowEmails$() {
    return this.campaigns$(true);
  }

  private customFieldsCache = new Map<string, {
    data$: Observable<any>,
    expiry$: Subject<void>
  }>();

  getCustomFields(
    limit: number = 5,
    offset: number = 0,
    cacheDuration: number = 5 * 60 * 1000 // 5 minuti in millisecondi
  ){
    const cacheKey = `limit:${limit}|offset:${offset}`;

    if (!this.customFieldsCache.has(cacheKey)) {
      const expiry$ = new Subject<void>();

      timer(cacheDuration).subscribe(() => {
        expiry$.next();
        expiry$.complete();
        this.customFieldsCache.delete(cacheKey);
      });

      const data$ = this.http
        .get<Api.Response<{ fields: PlatformContactField[], total_custom_fields: number, total_predefined_fields: number }>>(
          `${this.apiPrefix}/customfields`, {
            params: {
              limit,
              offset
            }
          }
        ).pipe(
          map(res => ({
            ...res,
            data: {
              fields: res.data.fields,
              total_custom_fields: +res.data.total_custom_fields,
              total_predefined_fields: +res.data.total_predefined_fields
            }
          })),
          takeUntil(expiry$),
          shareReplay(1)
        );

      this.customFieldsCache.set(cacheKey, { data$, expiry$ });
    }

    return this.customFieldsCache.get(cacheKey).data$;
  }


  getCampaign(
    campaignId: number
  ) {
    return this
      .http
      .get<Api.Response<PlatformCampaign>>(
        `${this.apiPrefix}/campaign/${campaignId}`
      );
  }

  validateCampaignName(name: string) {
    return this
      .http
      .post<Api.Response<{ reason: string }>>(
        `${this.apiPrefix}/message/validate_name`, {
          name
        }, {
          context: skipModalError([400])
        }
      ).pipe(
        catchError(e => of({success: false}))
      );
  }

  updateCampaign(
    messageId: number | null,
    data: Partial<PlatformCampaign>
  ) {
    return this
      .http
      .request<Api.Response<PlatformCampaign>>(
        messageId ? 'PUT' : 'POST',
        `${this.apiPrefix}/campaign${messageId ? `/${messageId}` : ''}`,
        {body: data}
      );
  }

  cloneCampaign(
    messageId: number,
    flow_id: number
  ) {
    return this
      .http
      .post<Api.Response<PlatformCampaign>>(
        `${this.apiPrefix}/campaign/${messageId}/clone`,
        {
          flow_id
        }
      );
  }

  autoresponders$() {
    return this
      .http
      .get<Api.Response<PlatformMessage[]>>(
        `${this.apiPrefix}/autoresponder`
      );
  }

  lists$() {
    return this
      .http
      .get<Api.Response<PlatformList[]>>(
        `${this.apiPrefix}/list`
      );
  }

  comparators$() {
    if (!this._comparators$) {
      this._comparators$ = this
        .http
        .get<Api.Response<ContactFieldComporatorList>>(
          `${this.apiPrefix}/comparators`
        ).pipe(
          shareReplay(1)
        );
    }
    return this._comparators$
  }

  links(
    messageId: number
  ) {
    return this
      .http
      .get<Api.Response<PlatformLink[]>>(
        `${this.apiPrefix}/message/${messageId}/link`
      ).pipe(
        map<Api.Response<PlatformLink[]>, Api.Response<PlatformLink[]>>(res => ({
          ...res,
          ...res?.data && {
            data: res.data.filter(link => !link.url.startsWith('#'))
          }
        }))
      );
  }

  forms$() {
    return this
      .http
      .get<Api.Response<PlatformForm[]>>(
        `${this.apiPrefix}/form`
      );
  }


  segments$(listId: number) {
    return this
      .http
      .get<Api.Response<PlatformSegment[]>>(
        `${this.apiPrefix}/segment`
      )
      .pipe(
        map(res => res?.data),
        switchMap(segments => segments),
        filter(segment => segment.list_id === listId),
        toArray()
      );
  }

  contactFields(listId: number) {
    return this
      .http
      .get<Api.Response<PlatformContactField[]>>(
        `${this.apiPrefix}/list/${listId}/contactfields`
      );
  }

  senders() {
    return this
      .http
      .get<Api.Response<Sender[]>>(
        `${this.apiPrefix}/sender`
      );
  }


}
